import { Dispatch, SetStateAction, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import c from 'classnames';
import { SubmitButton, Switch } from '../../../../../components';
import ArrowButton from '../../../../../components/arrow-button';
import Info from '../../../../../components/channel-configuration/info';
import { EditIcon } from '../../../../../components/edit-icon';
import { ChannelTypeInternal, UserPermissionType } from '../../../../../data-access/gql-types/graphql';
import { useUserPermissions } from '../../../../../hooks/user/use-user-permissions';
import { ROUTES } from '../../../../../routes/routes';
import { useDevicesAndChannelsContext } from '../../../context/devices-and-channels';
import { ChannelBinarySensorType, ChannelGateTypeOrUndefined, ChannelSensorType } from '../../../types';
import ConfigurationName from '../components/configuration-name';
import { availableIcons } from '../components/icon-edit/available-icons';
import ConfigurationGeneralPersonalization from '../general-personalization';
import { useConfigurationForm } from '../hooks/use-configuration-form';
import { InfoPageType } from '../types';

type ChannelGeneralConfigurationProps = {
  setInfoPage: Dispatch<SetStateAction<InfoPageType>>;
};

const ChannelGeneralConfiguration: React.FC<ChannelGeneralConfigurationProps> = ({ setInfoPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('configuration');
  const { t: tc } = useTranslation('common');
  const { channelType } = useParams<{ channelType: ChannelTypeInternal }>();
  const { channel, deviceList } = useDevicesAndChannelsContext();
  const { permissions } = useUserPermissions();
  const { formMethods, onSubmit, submitActive, hiddenValue, handleChangeVisibility } = useConfigurationForm({
    channel,
  });
  const { icon_name } = formMethods.watch();
  const gateKind = (channel?.data as ChannelGateTypeOrUndefined)?.gateKind;

  const isDeviceAvailable = useMemo(() => {
    if (!channel) return false;
    return (
      deviceList.map((device) => device.id).includes(channel?.deviceId) &&
      !new URLSearchParams(location.search).get('redirect') &&
      permissions[UserPermissionType.DeviceList]
    );
  }, [deviceList, channel, permissions, location.search]);

  const iconList = useMemo(() => {
    if (!channelType) return [];

    switch (channelType) {
      case ChannelTypeInternal.Gate:
        return availableIcons(channelType, { gateKind });
      case ChannelTypeInternal.Sensor:
        return availableIcons(channelType, { sensorType: (channel?.data as ChannelSensorType)?.sensorType });
      case ChannelTypeInternal.BinarySensor:
        return availableIcons(channelType, {
          binarySensorType: (channel?.data as ChannelBinarySensorType)?.sensorType,
        });
      default:
        return availableIcons(channelType);
    }
  }, [channelType, gateKind]);

  const handleChangeIcon = (value: string) => formMethods.setValue('icon_name', value);

  const goToDevice = () => navigate(ROUTES.DeviceSettings(channel?.deviceId));

  return (
    <>
      {isDeviceAvailable ? <ArrowButton title={t('deviceSettings')} onClick={goToDevice} /> : null}
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <hr className={c({ 'm-t-0': !isDeviceAvailable })} />
          <ConfigurationName label={t('configurationNameLabel')} placeholder={t('configurationName')} />
          {iconList.length ? (
            <EditIcon
              currentIcon={icon_name}
              iconList={iconList}
              handleChangeIcon={handleChangeIcon}
              header={t('channelIcon')}
            />
          ) : null}
          <hr />
          <Info title={t('visibility')} onClick={() => setInfoPage(InfoPageType.VISIBILITY)} />
          <div className="row_container">
            <p>{t('hideNotUsed')}</p>
            <Switch checked={hiddenValue} onChange={handleChangeVisibility} />
          </div>
          <ConfigurationGeneralPersonalization />

          <SubmitButton disabled={!submitActive}>{tc('buttons.save')}</SubmitButton>
        </form>
      </FormProvider>
    </>
  );
};

export default ChannelGeneralConfiguration;
